// ============================================================
// applyHits/entry - the attack-result entry, and the one tab write.
//
// A horde loses magnitude, anyone else loses wounds; both write one
// `attack-result` entry (with its structured twin), tag the info lines
// beneath it with the attack's group, sync every entry, and write the tab
// once: countdown lines, the entry, then the info lines.
// ============================================================
import { noiseOf, signatureOf } from '../../engine/stealth/noise';
import { coverApOf } from '../attack/beatFacts';
import { processHordeDamage } from '../../engine/postHitEffects';
import { buildStructuredEntry } from '../../engine/structuredNarrative';
import type { NarrativeInput } from '../../engine/narrativeFormatter';
import type { DefenceStepResult } from '../../engine/defenceChain';
import type { ForceField, NarrativeEntry, Tab, Target } from '../../types';
import { narrEvent, type HitLedger } from './ledger';
import { attackBeat } from '../../engine/attackBeat';

/**
 * Monotonic suffix for an attack group's id - deliberately NOT
 * Math.random(), for the reason `engine/roster/factories.ts` states and
 * this file was violating.
 *
 * The E2E harness stubs Math.random() with a seeded dice sequence, so
 * every id minted here stole a die and shifted every subsequent roll. It
 * cost a real diagnosis: `turn-effects.spec.js` seeded a d100 of 1 for an
 * extinguish test and got 41, which is the sequence WRAPPING to its first
 * value - applying the fire damage had minted an id in between.
 *
 * A counter plus the timestamp is unique for the same reason the roster's
 * ids are: two entries in one millisecond still differ.
 */
let attackGroupCounter = 0;
function nextAttackGroupId(tabId: string, timestamp: number): string {
  attackGroupCounter += 1;
  return `${tabId}-${timestamp}-${attackGroupCounter.toString(36)}`;
}

/** A defence chain step as the log serialises it. */
export type ChainStepSummary = ReturnType<typeof summarizeStep>;

function summarizeStep(s: DefenceStepResult) {
  return {
    type: s.step.type,
    // A table ruling typed on the banner names itself in the log (v8.366.0).
    label:
      (s.unnaturalNote ? `${s.step.label} (${s.unnaturalNote})` : s.step.label) +
      (s.step.manualMod ? ` [table ruling ${s.step.manualMod > 0 ? '+' : ''}${s.step.manualMod}]` : ''),
    roll: s.roll,
    skill: s.step.skill,
    success: s.success,
    dos: s.dos,
    dof: s.dof,
    avoided: s.avoided,
    counterAttackTriggered: s.counterAttackTriggered,
  };
}

/** The defence chain's steps as serialisable summaries for the narrative. */
export function summarizeChain(steps: DefenceStepResult[] | undefined): ChainStepSummary[] {
  return (steps || []).map(summarizeStep);
}

/**
 * What one kind of target reports: the entry's own fields (horde: the
 * magnitude, then wounds as they stand), the structured twin's, and the
 * patch its tab takes.
 */
interface Tally {
  horde: boolean;
  entryFields: Record<string, unknown>;
  structuredFields: Record<string, unknown>;
  patch: (target: Target) => Partial<Target>;
}

/** A horde: magnitude lost, and the horde's own lines logged as events. */
export function hordeTally(ledger: HitLedger): Tally {
  const { data, tgt, tab } = ledger;
  const { magDmg, magAfter, narrativeNotes } = processHordeDamage(
    data.hitsAfterDefence,
    data.dos,
    ledger.sp,
    tgt.horde?.magnitude || 0,
    tgt.horde?.maxMagnitude || 30,
    tgt,
    data.areaRolled?.shape === 'blast' ? data.areaRolled.radius : 0,
  );
  narrativeNotes.forEach((n) => narrEvent(ledger, { name: 'Horde', icon: '👹', result: n }));
  const magMax = tgt.horde?.maxMagnitude || 30;
  return {
    horde: true,
    entryFields: { magDmg, magAfter, magMax, woundsAfter: tab.target.wounds.current, woundsMax: tab.target.wounds.max },
    structuredFields: { isHorde: true, magDmg, magAfter, magMax },
    patch: (t) => ({ horde: { ...t.horde, magnitude: magAfter } }),
  };
}

/**
 * Anyone else: wounds lost, regeneration first. A FLOORED CHARACTER GOES
 * NEGATIVE LIKE ANYONE ELSE - clamping him to zero made the survival rules
 * visible in the one place they must not be, and threw away the severity
 * reading a GM uses. He is kept alive at the sites that kill.
 */
export function woundsTally(ledger: HitLedger): Tally {
  const { data, tab } = ledger;
  const regenAmt = data.cdResult?.regenHealed || 0;
  const woundsAfter = Math.min(tab.target.wounds.current + regenAmt - ledger.totalDamage, tab.target.wounds.max);
  const fields = { woundsAfter, woundsMax: tab.target.wounds.max };
  return {
    horde: false,
    entryFields: fields,
    structuredFields: fields,
    patch: (t) => ({ wounds: { ...t.wounds, current: woundsAfter } }),
  };
}

/** The structured twin of the entry. Declarative: the formatter's input, field for field. */
function structuredOf(ledger: HitLedger, tally: Tally, steps: ChainStepSummary[]) {
  const { data } = ledger;
  return buildStructuredEntry(
    {
      type: 'attack-result',
      slotName: data.slot.weaponName,
      fireMode: data.fireMode,
      targetName: ledger.tName,
      totalDamage: ledger.totalDamage,
      ...tally.structuredFields,
      skillBreakdown: data.skillBreakdown,
      result: {
        success: true,
        attackRoll: data.attackRoll,
        modifiedSkill: data.modifiedSkill,
        dos: data.dos,
        totalHits: data.totalHits,
        hitsAfterDefence: data.hitsAfterDefence,
        dodgeResult: data.dodgeResult,
        parryResult: data.parryResult,
        defenceChainSteps: steps.length > 0 ? steps : undefined,
        specialNotes: data.specialNotes || [],
      },
    } as unknown as NarrativeInput,
    ledger.containedEvents,
  );
}

/** The attack-result entry. Declarative: the stored entry, field for field, in its historic order. */
function attackEntry(ledger: HitLedger, tally: Tally, steps: ChainStepSummary[], atkTs: number): NarrativeEntry {
  const { data } = ledger;
  return {
    type: 'attack-result' as const,
    ...(tally.horde ? { isHorde: true } : {}),
    result: {
      success: true,
      attackRoll: data.attackRoll,
      modifiedSkill: data.modifiedSkill,
      dos: data.dos,
      dof: data.dof,
      totalHits: data.totalHits,
      hits: data.hits,
      hitsAfterDefence: data.hitsAfterDefence,
      dodgeResult: data.dodgeResult,
      parryResult: data.parryResult,
      defenceChainSteps: steps.length > 0 ? steps : undefined,
      jam: false,
      specialNotes: data.specialNotes || [],
      overheatDmg: data.overheatDmg || 0,
    },
    slotName: data.slot.weaponName,
    fireMode: data.fireMode,
    targetName: ledger.tName,
    totalDamage: ledger.totalDamage,
    ...tally.entryFields,
    timestamp: atkTs,
    // Who struck and how, for the display's beat (engine/attackBeat).
    beat: attackBeat(data.actorId, data.slot.weaponType === 'melee', 'landed', ledger.tabId, atkTs, {
      noise: noiseOf(data.slot),
      signature: signatureOf(data.slot),
      specials: data.slot.specialRules,
      looks: data.slot.looks,
      ...data.beatKind,
      coverAp: coverApOf(data.hitsAfterDefence),
    }),
    // WHICH VOLLEY THIS BELONGS TO. Null for an ordinary attack; shared by
    // every victim of one blast or cone, so the log can draw them as one
    // thing rather than five unrelated lines (v8.155.0).
    volleyId: data.volleyId ?? null,
    volleySize: data.volleySize ?? null,
    structured: structuredOf(ledger, tally, steps),
  } as NarrativeEntry;
}

/** Tag the entry and the lines under it with the attack's group, for CombatLogPanel suppression. */
function tagGroup(atkEntry: NarrativeEntry, lines: NarrativeEntry[], attackGroupId: string, atkTs: number): void {
  (atkEntry as NarrativeEntry & { attackGroupId?: string }).attackGroupId = attackGroupId;
  for (const e of lines) {
    (e as NarrativeEntry & { parentAttackTs?: number }).parentAttackTs = atkTs;
    (e as NarrativeEntry & { attackGroupId?: string }).attackGroupId = attackGroupId;
  }
}

/** What the commit needs besides the ledger. */
export interface CommitSink {
  syncNarrativeToFirestore: (entry: NarrativeEntry) => void;
  updateTab: (tabId: string, updater: (tab: Tab) => Tab) => void;
}

/**
 * Write the attack: the entry, its lines tagged with the attack's group
 * (for CombatLogPanel suppression), every entry synced, the tab written once.
 */
export function commitAttack(
  ledger: HitLedger,
  tally: Tally,
  forceFieldUpdate: ForceField | null,
  sink: CommitSink,
): void {
  const steps = summarizeChain(ledger.data.defenceChainSteps);
  const cdNarr = ledger.data.cdResult?.narrativeEntries || [];
  const atkTs = Date.now();
  const attackGroupId = nextAttackGroupId(ledger.tabId, atkTs);
  const atkEntry = attackEntry(ledger, tally, steps, atkTs);
  tagGroup(atkEntry, ledger.extraNarrative, attackGroupId, atkTs);
  cdNarr.forEach((e) => sink.syncNarrativeToFirestore(e));
  sink.syncNarrativeToFirestore(atkEntry);
  ledger.extraNarrative.forEach((e) => sink.syncNarrativeToFirestore(e));
  const { newConds, newArmour, extraNarrative } = ledger;
  sink.updateTab(ledger.tabId, (t) => ({
    ...t,
    target: {
      ...t.target,
      ...tally.patch(t.target),
      conditions: newConds,
      armour: newArmour,
      ...(forceFieldUpdate ? { forceField: forceFieldUpdate } : {}),
    },
    narrativeLog: [...t.narrativeLog, ...cdNarr, atkEntry, ...extraNarrative],
  }));
}
